import { PriceFunc, PriceElemsData, PriceSegment, getConstrElems } from './Prices';
import { CustomPricesRecords } from '@icc/common/custom-price/CustomPrice';
import * as camelCase from 'camelcase';
import { Injectable, Inject } from '@angular/core';
import { PriceException } from './price-exception';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/common/config';;
import { ColorsDefaultsService } from '@icc/common/colors/colors-defaults.service';
import { Common } from '@icc/common/Common';
import { core } from '@icc/common/Core';

@Injectable()
export class PriceColorsService {
    constructor(
        private colorsDefaultsService: ColorsDefaultsService,
        @Inject(APP_CONFIG) private config: AppConfigFactory
    ) {}

    /**
     * Zwraca najwyższą dopłatę procentową dla koloru.
     * @param  {object} colorGroups Grupy kolorów
     * @param  {object} color       Kolor
     * @param  {string} prop        Nazwa pola zawierającego dopłatę.
     * @return {number}             Dopłata procentowa dla koloru
     */
    getMaxColorGroupFactor({
        colorGroups,
        color,
        prop,
        system,
        wood,
        sash,
        sashId,
        divId,
        drawData,
        sashes,
        mullions,
        customPrice,
        side,
    }: {
        colorGroups;
        color;
        prop;
        system;
        wood;
        sash;
        sashId;
        divId;
        drawData;
        sashes;
        mullions;
        customPrice: CustomPricesRecords;
        side?;
    }) {
        let colorFactor = null;
        let colorFactorExtra = null;
        let colorFactorExtraProfile = 0;
        if (!Common.isArray(sash)) {
            return NaN;
        }
        if (!colorGroups) {
            colorGroups = [];
        }
        colorGroups = colorGroups.filter(
            el =>
                Common.isArray(el.systems)
                && el.systems.indexOf(system.id) > -1
                && el.target.indexOf('price') > -1
                && (side == null || el.sides.includes(side))
                && (system.type !== 'wood'
                    || (Common.isArray(el.woodTypes) && el.woodTypes.indexOf(wood.id) > -1))
        );
        if (Common.isArray(color.groups)) {
            for (let i = 0; i < color.groups.length; i++) {
                let group = core.fIdO(colorGroups, color.groups[i]) as any;
                if (color.groups[i] === 'RAL') {
                    group = this.getRALGroup(colorGroups);
                }
                if (Common.isDefined(group)) {
                    let customColorFactor = null;
                    if (Common.isObject(customPrice) && customPrice[group.id]) {
                        customColorFactor = customPrice[group.id].getPrice(prop);
                    }
                    if (customColorFactor != null && customColorFactor >= colorFactor) {
                        colorFactor = customColorFactor;
                    }
                    if (customColorFactor == null && Common.isDefined(group[prop])) {
                        if (group[prop] != null && parseFloat(group[prop]) >= colorFactor) {
                            colorFactor = parseFloat(group[prop]);
                        }
                    }
                    if (Common.isDefined(group.prices_extra)) {
                        const pricesExtra = group.prices_extra
                            ? core.parseJson(group.prices_extra)
                            : [];
                        sash.forEach(s => {
                            const colorSashFactor = pricesExtra
                                .filter(
                                    p => p.sashTypes && p.sashTypes.map(Number).indexOf(~~s) > -1
                                )
                                .reduce((prev, cur, index) => {
                                    if (Common.isObject(customPrice) && customPrice[group.id]) {
                                        const customColorSashFactor = customPrice[
                                            group.id
                                        ].getPrice('prices_extra', index, camelCase(prop));
                                        return prev < customColorSashFactor
                                            ? customColorSashFactor
                                            : prev;
                                    } else {
                                        return prev < cur[camelCase(prop)]
                                            ? cur[camelCase(prop)]
                                            : prev;
                                    }
                                }, 0);
                            if (colorSashFactor > colorFactorExtra) {
                                colorFactorExtra = colorSashFactor;
                            }
                        });
                    }
                    if (Common.isDefined(group.prices_extra_profiles)) {
                        const pricesExtraProfiles = group.prices_extra_profiles
                            ? core.parseJson(group.prices_extra_profiles)
                            : [];
                        const profilesIds = [];
                        sashId.forEach(s => {
                            const profilesForSash = this.getProfilesForSash(s, drawData, sashes);
                            profilesIds.push(
                                ...profilesForSash.filter(p => !profilesIds.includes(p))
                            );
                        });
                        divId.forEach(s => {
                            const profileForMullion = this.getProfileForMullion(s, mullions);
                            if (!profilesIds.includes(profileForMullion)) {
                                profilesIds.push(profileForMullion);
                            }
                        });
                        colorFactorExtraProfile += profilesIds.reduce((prev, profileId) => {
                            const profileSashFactor = pricesExtraProfiles
                                .filter(p => p.profiles.map(Number).indexOf(~~profileId) > -1)
                                .reduce((p, cur, index) => {
                                    let factor = cur[camelCase(prop)];
                                    if (Common.isObject(customPrice) && customPrice[group.id]) {
                                        const customProfileSashFactor = customPrice[
                                            group.id
                                        ].getPrice('prices_extra', index, camelCase(prop));
                                        if (customProfileSashFactor !== null) {
                                            factor = customProfileSashFactor;
                                        }
                                    }
                                    return p + factor;
                                }, 0);
                            return prev + profileSashFactor;
                        }, 0);
                    }
                }
            }
        }
        if (colorFactor == null && colorFactorExtra == null) {
            return NaN;
        }
        return (colorFactorExtra || colorFactor) + colorFactorExtraProfile;
    }

    /**
     * Zwraca typ kolorów dla kolorów
     *
     * zew/wew
     * @param  {object} colors Kolory
     * @param  {number} sashesCount Liczba skrzydeł
     * @return {string}        Typ kolorów
     */
    getColorTypeForColors(colors, sashesCount, onlyFrame = false) {
        let colorType = 'Bicolor';
        if (Common.isObject(colors)) {
            const outer = this.colorsDefaultsService.getColorValue(colors, 'outer', onlyFrame);
            const inner = this.colorsDefaultsService.getColorValue(colors, 'inner', onlyFrame);
            const core = this.colorsDefaultsService.getColorValue(colors, 'core', onlyFrame);

            // Biały
            if (
                this.colorsDefaultsService.getDefaultColorType('White', 'outer') === outer
                && this.colorsDefaultsService.getDefaultColorType('White', 'inner') === inner
                && this.colorsDefaultsService.getDefaultColorType('White', 'core') === core
            ) {
                colorType = 'White';
                // Krem w masie
            } else if (
                this.colorsDefaultsService.getDefaultColorType('Cream', 'outer') === outer
                && this.colorsDefaultsService.getDefaultColorType('Cream', 'inner') === inner
                && this.colorsDefaultsService.getDefaultColorType('Cream', 'core') === core
            ) {
                colorType = 'Cream';
                // zewnętrzny
            } else if (
                ([
                    'WHITE',
                    this.colorsDefaultsService.getDefaultColorType('Outer', 'outer'),
                ] as any).includes(outer)
                && this.colorsDefaultsService.getDefaultColorType('Outer', 'inner') === inner
                && this.colorsDefaultsService.getDefaultColorType('Outer', 'core') === core
            ) {
                colorType = 'Outer';
                // wewnętrzny
            } else if (
                this.colorsDefaultsService.getDefaultColorType('Inner', 'outer') === outer
                && ([
                    'WHITE',
                    this.colorsDefaultsService.getDefaultColorType('Inner', 'inner'),
                ] as any).includes(inner)
                && this.colorsDefaultsService.getDefaultColorType('Inner', 'core') === core
            ) {
                colorType = 'Inner';
                // Kolor obustronny
            } else if (
                this.colorsDefaultsService.getDefaultColorType('Bilateral', 'outer') === outer
                && this.colorsDefaultsService.getDefaultColorType('Bilateral', 'inner') === inner
                && colors.frame.outer.id === colors.frame.inner.id
            ) {
                colorType = 'Bilateral';
                // Bicolor
            } else {
                colorType = 'Bicolor';
            }
        } else {
            if (sashesCount > 0) {
                throw new PriceException('Niepoprawne kolory', 'color_type');
            }
        }
        return colorType;
    }

    /**
     * Zwraca dopłatę procentową za RAL.
     * @param  {array}  colorGroups   Grupy kolorów
     * @return {number}               Cena po dopłatach
     */
    getRALGroup(colorGroups) {
        colorGroups = core.objToArray(colorGroups);
        const RALGroup = core.fIdO<any>(colorGroups, 'ral', 'code');
        return RALGroup;
    }

    getColorFactor({
        NoPriceCauses,
        colorType,
        colorGroups,
        colorGroupsPrices,
        colors,
        system,
        wood,
        sash,
        sashId,
        divId,
        drawData,
        sashes,
        mullions,
        customPrice,
    }: {
        NoPriceCauses;
        colorType;
        colorGroups;
        colorGroupsPrices;
        colors;
        system;
        wood;
        sash;
        sashId;
        divId;
        drawData;
        sashes;
        mullions;
        customPrice?: CustomPricesRecords;
    }) {
        let colorFactor = 0;
        if (colorType && !['White', 'Cream', '3D', 'Alushell'].includes(colorType)) {
            colorFactor = this.getColorGroupsPrice(colorGroupsPrices, colors);
        }
        switch (colorType) {
            case 'White':
            case 'Cream':
                colorFactor = this.getMaxColorGroupFactor({
                    colorGroups,
                    color: colors.frame.core,
                    prop: 'price_factor_both',
                    system,
                    wood,
                    sash,
                    sashId,
                    divId,
                    drawData,
                    sashes,
                    mullions,
                    customPrice,
                });
                if (isNaN(colorFactor)) {
                    NoPriceCauses.push('white cream color');
                }
                break;
            case 'Outer':
                if (isNaN(colorFactor)) {
                    colorFactor = this.getMaxColorGroupFactor({
                        colorGroups,
                        color: colors.frame.outer,
                        prop: 'price_factor_out',
                        system,
                        wood,
                        sash,
                        sashId,
                        divId,
                        drawData,
                        sashes,
                        mullions,
                        customPrice,
                    });
                }
                if (isNaN(colorFactor)) {
                    NoPriceCauses.push('outer color');
                }
                break;
            case 'Inner':
                if (isNaN(colorFactor)) {
                    colorFactor = this.getMaxColorGroupFactor({
                        colorGroups,
                        color: colors.frame.inner,
                        prop: 'price_factor_in',
                        system,
                        wood,
                        sash,
                        sashId,
                        divId,
                        drawData,
                        sashes,
                        mullions,
                        customPrice,
                    });
                }
                if (isNaN(colorFactor)) {
                    NoPriceCauses.push('inka');
                }
                break;
            case 'Bilateral':
                if (isNaN(colorFactor)) {
                    colorFactor = this.getMaxColorGroupFactor({
                        colorGroups,
                        color: colors.frame.inner,
                        prop: 'price_factor_both',
                        system,
                        wood,
                        sash,
                        sashId,
                        divId,
                        drawData,
                        sashes,
                        mullions,
                        customPrice,
                    });
                }
                if (isNaN(colorFactor)) {
                    NoPriceCauses.push('bilateral color');
                }
                break;
            case 'Bicolor': {
                if (isNaN(colorFactor)) {
                    const colorFactorIn = this.getMaxColorGroupFactor({
                        colorGroups,
                        color: colors.frame.inner,
                        prop: 'price_factor_duo',
                        system,
                        wood,
                        sash,
                        sashId,
                        divId,
                        drawData,
                        sashes,
                        mullions,
                        customPrice,
                    });
                    const colorFactorOut = this.getMaxColorGroupFactor({
                        colorGroups,
                        color: colors.frame.outer,
                        prop: 'price_factor_duo',
                        system,
                        wood,
                        sash,
                        sashId,
                        divId,
                        drawData,
                        sashes,
                        mullions,
                        customPrice,
                    });
                    colorFactor =
                        colorFactorOut > colorFactorIn || isNaN(colorFactorOut)
                            ? colorFactorOut
                            : colorFactorIn;
                }
                if (isNaN(colorFactor)) {
                    NoPriceCauses.push('bicolor');
                }
                break;
            }
            case '3D':
                colorFactor = NaN;
                NoPriceCauses.push('3d color');
                break;
            case 'Alushell':
                colorFactor = this.getMaxColorGroupFactor({
                    colorGroups,
                    color: colors.frame.alushell,
                    prop: 'price_factor_out',
                    system,
                    wood,
                    sash,
                    sashId,
                    divId,
                    drawData,
                    sashes,
                    mullions,
                    customPrice,
                    side: 'F|A',
                });
                if (isNaN(colorFactor)) {
                    NoPriceCauses.push('bilateral color');
                }
                break;
        }
        return colorFactor;
    }

    getColorGroupsPrice(colorGroupsPrices, colors) {
        return colorGroupsPrices
            .filter(
                el =>
                    colors.frame.inner.groups
                    && colors.frame.inner.groups.indexOf(el.window_color_group_inner_id) > -1
                    && colors.frame.outer.groups
                    && colors.frame.outer.groups.indexOf(el.window_color_group_outer_id) > -1
            )
            .reduce((prev, curr) => {
                if (isNaN(prev) || Number(curr.price) > prev) {
                    prev = Number(curr.price);
                }
                return prev;
            }, NaN);
    }

    /**
     * Dopłata procentowa za kolor.
     *
     * @param {number} price         Cena wejściowa
     * @param {object} PriceElems    Składowe ceny
     * @param {array}  NoPriceCauses Powody braku wyceny
     * @param {array}  constrElems   Lista elementów konstrukcyjnych, do przeliczenia przez dopłatę.
     * @param {array}  colors        Kolory
     * @param {array}  colorGroups   Grupy kolorów
     * @param {number} sashesCount   Liczba skrzydeł w konstrukcji
     * @return {number} Cena z dopłatą
     */
    @PriceFunc({
        shortName: 'color',
        data: {
            colors: 'conf.Colors',
            colorGroups: 'data.windowColorGroups',
            colorGroupsPrices: 'data.windowColorGroupsPrices',
            sashesCount: 'conf.Sashes.length',
            system: 'conf.System',
            wood: 'conf.Wood',
            drawData: 'conf.drawData',
            sashes: 'conf.Sashes',
            customPrice: 'price.WindowColorGroup',
            mullions: 'conf.Mullions',
        },
    })
    suppColor(
        { PriceStack, NoPriceCauses }: PriceElemsData,
        {
            colors,
            colorGroups,
            colorGroupsPrices,
            sashesCount,
            system,
            wood,
            drawData,
            sashes,
            mullions,
            customPrice,
        }: {
            colors;
            colorGroups;
            colorGroupsPrices;
            sashesCount;
            system;
            wood;
            drawData;
            sashes;
            mullions;
            customPrice: CustomPricesRecords;
        }
    ): PriceSegment[] {
        const constrElems = getConstrElems(this.config().IccConfig, system, 'color');
        const colorType = this.getColorTypeForColors(colors, sashesCount);
        const priceSashes = PriceStack.filter(
            s => constrElems.indexOf('sashes') > -1 && s.type === 'sashes'
        );
        const divs = PriceStack.filter(
            s => constrElems.indexOf('dividers') > -1 && s.type === 'dividers'
        );
        const priceSegments: PriceSegment[] = [];
        const divFactors: any = [];
        if (!colorGroups || colorGroups.length === 0) {
            return [];
        }
        if (colorGroupsPrices == null) {
            colorGroupsPrices = [];
        }
        const colorFactorDefault = this.getColorFactor({
            NoPriceCauses,
            colorType,
            colorGroups,
            colorGroupsPrices,
            colors,
            system,
            wood,
            sash: [],
            sashId: [],
            divId: [],
            drawData,
            sashes,
            mullions,
            customPrice,
        });
        priceSashes.forEach(s => {
            const colorFactor = this.getColorFactor({
                NoPriceCauses,
                colorType,
                colorGroups,
                colorGroupsPrices,
                colors,
                system,
                wood,
                sash: s.data.types,
                sashId: s.data.sashIds,
                divId: s.data.divIds || [],
                drawData,
                sashes,
                mullions,
                customPrice,
            });
            if (s.data && s.data.divIds) {
                divFactors.push({
                    divs: s.data.divIds || [],
                    factor: colorFactor,
                });
            }
            priceSegments.push({
                type: 'color',
                baseValue: !isNaN(colorFactor) ? 1 + colorFactor / 100 : null,
                value: !isNaN(colorFactor) ? 1 + colorFactor / 100 : null,
                valueType: 'percent',
                data: {},
                to: s.id,
            });
        });
        divs.forEach(d => {
            const factor = divFactors.filter(df => df.divs.indexOf(d.data.divId) > -1);
            if (factor.length > 0) {
                priceSegments.push({
                    type: 'color',
                    baseValue: !isNaN(factor[0].factor) ? 1 + factor[0].factor / 100 : null,
                    value: !isNaN(factor[0].factor) ? 1 + factor[0].factor / 100 : null,
                    valueType: 'percent',
                    data: {},
                    to: d.id,
                });
            }
        });
        priceSegments.push({
            type: 'color',
            baseValue: !isNaN(colorFactorDefault) ? 1 + colorFactorDefault / 100 : null,
            value: !isNaN(colorFactorDefault) ? 1 + colorFactorDefault / 100 : null,
            valueType: 'percent',
            data: {},
            to: constrElems.filter(s => s !== 'sashes' && s !== 'dividers'),
        });
        return priceSegments;
    }

    /**
     * Dopłata za typ drzewa
     * @param  {Number} price           Cena
     * @param  {Object} PriceElems      Wycena
     * @param  {Array} constrElems      Elementy konstrukcyjne
     * @param  {Object} wood            Drzewo
     * @return {Number}                 Cena
     */
    @PriceFunc({
        shortName: 'wood',
        data: {
            wood: 'conf.Wood',
            system: 'conf.System',
            customPrice: 'price.WoodType',
        },
    })
    suppWoodType(
        { PriceElems }: PriceElemsData,
        { wood, system, customPrice }: { wood; system; customPrice: CustomPricesRecords }
    ): PriceSegment[] {
        let factors = 1;
        let woodCharge = 0;
        if (system.type !== 'wood') {
            return [];
        }
        if (Common.isObject(wood)) {
            if (Common.isObject(customPrice) && customPrice[wood.id]) {
                woodCharge = customPrice[wood.id].getPrice('charge');
            } else {
                woodCharge = parseFloat(wood.charge);
            }
            PriceElems.wood = {
                id: wood.id,
                factor: woodCharge,
                name: wood.name,
            };
            factors *= 1 + woodCharge / 100;
        }

        return [
            {
                type: 'wood',
                baseValue: factors,
                value: factors,
                valueType: 'percent',
                data: {
                    id: wood.id,
                    name: wood.name,
                },
            },
        ] as PriceSegment[];
    }

    private getProfilesForSash(sashId, drawData, sashes) {
        const profilesForSash = [];
        const sashData = drawData.sash.find(o => o.sashId === sashId);
        if (sashData) {
            sashData.parallel.poly.forEach((frameIndex, sashIndex) => {
                if (Common.isNumber(frameIndex)) {
                    const frameProfileId = sashData.parallel.profiles[frameIndex];
                    if (!profilesForSash.includes(frameProfileId)) {
                        profilesForSash.push(frameProfileId);
                    }
                }
            });
            const sash = sashes.find(s => s.id === sashId);
            if (sash.type.type !== 'F') {
                ['left', 'right', 'top', 'bottom']
                    .filter(side => sash.frame[side])
                    .forEach(side => {
                        const sashProfileId = sash.frame[side].profileId;
                        if (!profilesForSash.includes(sashProfileId)) {
                            profilesForSash.push(sashProfileId);
                        }
                    });
            }
        }

        return profilesForSash;
    }

    private getProfileForMullion(mullionId, mullions) {
        const mullion = mullions.find(m => m.id === mullionId);
        return mullion.profileId;
    }
}
